import type { AppAccessSummary } from '../types';

export const MEMBERS_MANAGE_CAPABILITY = 'members:manage';
export const PROJECT_SETTINGS_WRITE_CAPABILITY = 'project:settings:write';
export const CONVERSATION_CREATE_CAPABILITY = 'conversation:create';

export function hasAppCapability(access: AppAccessSummary | null | undefined, capability: string): boolean {
  return Boolean(access?.admin_bypass || access?.capabilities.includes(capability));
}

export function canManageProject(access: AppAccessSummary | null | undefined): boolean {
  return hasAppCapability(access, PROJECT_SETTINGS_WRITE_CAPABILITY);
}

export function canManageMembers(access: AppAccessSummary | null | undefined): boolean {
  return hasAppCapability(access, MEMBERS_MANAGE_CAPABILITY);
}

/**
 * Conversation creation is authorized for every selected project. Keep the
 * app picker fail-closed rather than treating project membership as enough to
 * create a conversation.
 */
export function canCreateConversation(access: AppAccessSummary | null | undefined): boolean {
  return hasAppCapability(access, CONVERSATION_CREATE_CAPABILITY);
}

export function isProjectMember(me: { project_access?: boolean } | null | undefined): boolean {
  return me?.project_access === true;
}

export function invitationBrowserPath(acceptancePath: string | null | undefined, token: string): string {
  if (acceptancePath?.startsWith('/')) {
    return acceptancePath;
  }
  return `/invitations#token=${encodeURIComponent(token)}`;
}

export function effectiveAccessSummary(role: string, capabilities: string[]): string {
  const normalizedCapabilities = new Set(capabilities);
  const restricted = [
    ['secrets:manage', 'manage secrets'],
    ['deploy:run', 'run deployments'],
    ['release:run', 'run releases'],
    [MEMBERS_MANAGE_CAPABILITY, 'manage members']
  ]
    .filter(([capability]) => !normalizedCapabilities.has(capability))
    .map(([, label]) => `cannot ${label}`);

  const baseline = role === 'viewer'
    ? 'Can view project metadata and permitted conversations'
    : 'Can collaborate within the project capabilities granted by the server';
  return restricted.length > 0 ? `${baseline}; ${restricted.join(', ')}.` : `${baseline}.`;
}
